/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/**
 * Breadcrumb label overrides (DCP-296).
 *
 * The generic {@link Breadcrumbs} component maps each URL path SEGMENT to a
 * label via a static route table. Dynamic segments (a facility id, an org id, …)
 * have no static label, so the page that already owns that resource's data
 * registers a display label for the segment here — keeping `Breadcrumbs` a pure
 * presentational component that never fetches domain data itself.
 *
 * `overrides` is keyed by the SEGMENT VALUE (e.g. the facility id string), so
 * Breadcrumbs can look up a segment directly while rendering — or by a crumb's
 * whole PATH (`/settings/…`) to relabel a static segment on one route only.
 */
interface BreadcrumbContextValue {
  /** segment value → display label (e.g. `<facilityId>` → "Bay City"). */
  overrides: Record<string, string>
  /** Register/replace the label for a segment value. */
  setOverride: (key: string, label: string) => void
  /** Drop a segment's label (on the registering component's unmount). */
  removeOverride: (key: string) => void
}

/**
 * Safe defaults so BOTH the consumer ({@link Breadcrumbs}) and registrants
 * ({@link useSetBreadcrumb}) work even when no provider is mounted — e.g. an
 * isolated component test rendering a page or the breadcrumb bar directly. With
 * no provider there are simply no overrides and the setters are no-ops; nothing
 * crashes and the pre-existing "raw segment / static label" behaviour holds.
 */
const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  overrides: {},
  setOverride: () => {},
  removeOverride: () => {},
})

/**
 * Provider holding the live segment→label overrides. Wrap it around BOTH the
 * breadcrumb bar and the routed pages (done once in DashboardLayout) so a page
 * can register a label that the bar then renders.
 */
export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [overrides, setOverrides] = useState<Record<string, string>>({})

  // Returning `prev` unchanged when the label already matches avoids a needless
  // state update / re-render loop when a page re-renders with the same label.
  const setOverride = useCallback((key: string, label: string) => {
    setOverrides((prev) => (prev[key] === label ? prev : { ...prev, [key]: label }))
  }, [])

  const removeOverride = useCallback((key: string) => {
    setOverrides((prev) => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const value = useMemo(
    () => ({ overrides, setOverride, removeOverride }),
    [overrides, setOverride, removeOverride],
  )

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>
}

/** Current segment→label overrides — read by {@link Breadcrumbs}. */
export function useBreadcrumbOverrides(): Record<string, string> {
  return useContext(BreadcrumbContext).overrides
}

/**
 * Register a display label for a dynamic path segment for as long as the
 * calling component is mounted (DCP-296). Called by the page that owns the
 * resource, e.g. `useSetBreadcrumb(facilityId, facility?.name)`.
 *
 * A null/undefined `key` OR `label` registers nothing (e.g. while the resource
 * is still loading), so Breadcrumbs falls back to the raw segment until the
 * label is known. The label is removed on unmount, so navigating away doesn't
 * leak a stale override onto an unrelated route that happens to reuse the id.
 */
export function useSetBreadcrumb(
  key: string | undefined | null,
  label: string | undefined | null,
) {
  const { setOverride, removeOverride } = useContext(BreadcrumbContext)

  useEffect(() => {
    if (!key || !label) return
    setOverride(key, label)
    return () => removeOverride(key)
  }, [key, label, setOverride, removeOverride])
}
